import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Chip,
  CircularProgress,
  Snackbar,
  InputAdornment,
  IconButton,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Product } from '@/models/product';
import { ProductService } from '@/services/productService';
import './SmartProductForm.css';

export interface SmartProductFormData {
  item_code: string;
  item_name: string;
  rate: number;
  quantity: number;
  gst_percentage: number;
  hsn_code: number;
  poNumber: string;
  total: number;
}

export interface SmartProductFormProps {
  products: Product[];
  onProductAdd: (productId: string, quantity: number, poNumber: number) => void;
  onProductsRefresh?: () => Promise<void>;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  user?: any;
}

const SmartProductForm: React.FC<SmartProductFormProps> = ({
  products,
  onProductAdd,
  onProductsRefresh,
  disabled = false,
  loading = false,
  error,
  user
}) => {
                // Form state
                            const [formData, setFormData] = useState<SmartProductFormData>({
                              item_code: '',
                              item_name: '',
                              rate: 0,
                              quantity: 1,
                              gst_percentage: 18,
                              hsn_code: 0,
                              poNumber: '',
                              total: 0,
                            });

                // Refs for focusing inputs
                const [existingProduct, setExistingProduct] = useState<Product | null>(null);
                const itemCodeInputRef = useRef<HTMLInputElement | null>(null);
  const formDataRef = useRef<SmartProductFormData>(formData);

  // Prevents item_code useEffect from overwriting a product selected via Description dropdown
  const skipItemCodeLookup = useRef(false);
  const existingProductRef = useRef<Product | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Keep refs in sync with the latest form state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    existingProductRef.current = existingProduct;
  }, [existingProduct]);

  // Calculate total whenever rate or quantity changes
  useEffect(() => {
    const total = formData.rate * formData.quantity;
    setFormData(prev => ({ ...prev, total }));
  }, [formData.rate, formData.quantity]);

  // Check for existing product when item_code changes (via Part# field typing only)
  useEffect(() => {
    if (skipItemCodeLookup.current) {
      skipItemCodeLookup.current = false;
      return;
    }
    if (formData.item_code.trim()) {
      const product = products.find(p => p.item_code === formData.item_code);
      setExistingProduct(product || null);
      if (product) {
        setFormData(prev => ({
          ...prev,
          item_name: product.item_name,
          rate: product.rate,
          gst_percentage: product.gst_percentage,
          hsn_code: product.hsn_code,
        }));
      }
    } else {
      setExistingProduct(null);
    }
  }, [formData.item_code, products]);

  // Handle field changes
  const handleFieldChange = (field: keyof SmartProductFormData, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      formDataRef.current = next;
      return next;
    });
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate form
  const validateForm = (): { isValid: boolean; normalizedData: SmartProductFormData; errors: Record<string, string> } => {
    const currentFormData = formDataRef.current;
    const selectedProduct = existingProductRef.current ?? products.find((product) => {
      const itemCode = currentFormData.item_code.trim();
      const itemName = currentFormData.item_name.trim();
      return (itemCode && product.item_code === itemCode) || (itemName && product.item_name === itemName);
    });

    const normalizedData: SmartProductFormData = {
      ...currentFormData,
      item_code: currentFormData.item_code.trim() || selectedProduct?.item_code?.trim() || '',
      item_name: currentFormData.item_name.trim() || selectedProduct?.item_name?.trim() || '',
      rate: currentFormData.rate > 0 ? currentFormData.rate : selectedProduct?.rate ?? 0,
      gst_percentage: currentFormData.gst_percentage >= 0 ? currentFormData.gst_percentage : selectedProduct?.gst_percentage ?? 0,
      hsn_code: currentFormData.hsn_code || selectedProduct?.hsn_code || 0,
    };

    const errors: Record<string, string> = {};

    if (!normalizedData.item_code) {
      errors.item_code = 'Part Number is required';
    }
    if (!normalizedData.item_name) {
      errors.item_name = 'Part Description is required';
    }
    if (normalizedData.rate <= 0) {
      errors.rate = 'Rate must be greater than 0';
    }
    if (normalizedData.quantity <= 0) {
      errors.quantity = 'Quantity must be greater than 0';
    }
    if (normalizedData.gst_percentage < 0 || normalizedData.gst_percentage > 100) {
      errors.gst_percentage = 'GST percentage must be between 0 and 100';
    }

    setValidationErrors(errors);
    return { isValid: Object.keys(errors).length === 0, normalizedData, errors };
  };

  // Handle smart add
  const handleSmartAdd = async () => {
    const validationResult = validateForm();
    if (!validationResult.isValid) {
      return;
    }

    const submissionData = validationResult.normalizedData;

    setSubmitting(true);
    try {
      if (existingProduct) {
        // Case 1: Same data - add directly
        const isSameData =
          existingProduct.item_name === submissionData.item_name &&
          existingProduct.rate === submissionData.rate &&
          existingProduct.gst_percentage === submissionData.gst_percentage &&
          existingProduct.hsn_code === submissionData.hsn_code;

        if (isSameData) {
          addProductToInvoice(existingProduct._id, submissionData);
          showNotification('Product added successfully', 'success');
        } else {
          // Case 2: Other fields edited - update product then add
          const updateData = {
            item_name: submissionData.item_name,
            rate: submissionData.rate,
            gst_percentage: submissionData.gst_percentage,
            hsn_code: submissionData.hsn_code,
            updated_by: user?.username || 'ADMIN',
          };

          const response = await ProductService.updateProduct(existingProduct._id, updateData);
          if (response.success) {
            showNotification('Product updated and added successfully', 'success');
            await onProductsRefresh?.();
            addProductToInvoice(existingProduct._id, submissionData);
          } else {
            showNotification(response.error || 'Failed to update product', 'error');
            return;
          }
        }
      } else {
        // Case 3: New item_code - create new product then add
        const createData = {
          item_code: submissionData.item_code,
          item_name: submissionData.item_name,
          hsn_code: submissionData.hsn_code,
          rate: submissionData.rate,
          type: 'PRODUCT',
          unit: 'pc', // Default value
          gst_percentage: submissionData.gst_percentage,
          created_by: user?.username || 'ADMIN',
        };

        const response = await ProductService.createProduct(createData);
        if (response.success) {
          showNotification('New product created and added successfully', 'success');
          await onProductsRefresh?.();
          addProductToInvoice(response.data._id, submissionData);
        } else {
          showNotification(response.error || 'Failed to create product', 'error');
          return;
        }
      }
    } catch (error) {
      console.error('Error in smart add:', error);
    } finally {
      setSubmitting(false);
      resetForm();
    }
  };

  // Add product to invoice
  const addProductToInvoice = (productId: string, submissionData: SmartProductFormData) => {
    onProductAdd(
      productId,
      submissionData.quantity,
      Number.parseInt(submissionData.poNumber, 10) || 0
    );
  };

                // Reset form
              const resetForm = () => {
                setFormData({
                  item_code: '',
                  item_name: '',
                  rate: 0,
                  quantity: 1,
                  gst_percentage: 18,
                  hsn_code: 0,
                  poNumber: '',
                  total: 0,
                });
                setExistingProduct(null);
                setValidationErrors({});
                // Return focus to Part# input so user can quickly add next item
                requestAnimationFrame(() => {
                  try {
                    itemCodeInputRef.current?.focus();
                  } catch (e) {
                    // ignore focus failures
                  }
                });
              };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Get smart action type
  const getSmartActionType = () => {
    if (!existingProduct) return 'create';
                    if (existingProduct.item_name === formData.item_name &&
                    existingProduct.rate === formData.rate &&
                    existingProduct.gst_percentage === formData.gst_percentage &&
                    existingProduct.hsn_code === formData.hsn_code) {
                  return 'add';
                }
    return 'update';
  };

  const smartActionType = getSmartActionType();

  // Lightweight validity check to disable Add button until basic fields are valid
  const isFormValid = !!(
    formData.item_code.trim() &&
    formData.item_name.trim() &&
    formData.quantity > 0 &&
    formData.rate > 0 &&
    formData.gst_percentage >= 0 &&
    formData.gst_percentage <= 100
  );

  return (
    <Box className="smart-product-form">
                        {/* Header */}
                  <Box className="spf-form-header">
                    <Box className="spf-header-left">
                      <Typography variant="h6" className="spf-form-title">
                        Add Product
                      </Typography>
                    </Box>
                    <Box className="spf-header-actions">
                      <Tooltip title="Refresh Products">
                        <IconButton
                          size="small"
                          onClick={() => onProductsRefresh?.()}
                          disabled={disabled || loading}
                          className="spf-header-icon-button"
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>



      {/* Error Display */}
      {error && (
        <Alert severity="error" className="spf-error-alert">
          {error}
        </Alert>
      )}

      {/* Form Content */}
      <Box className="spf-form-content">
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Single Row - All Fields */}
            <Grid container spacing={1} className="spf-form-row">
          {/* Part Number */}
          <Grid item xs={6} sm={3} md={2}>
            <Autocomplete
              options={(() => {
                const uniqueCodes = new Map<string, { value: string; label: string; product: Product }>();
                products.forEach(p => {
                  if (p.item_code && !uniqueCodes.has(p.item_code)) {
                    uniqueCodes.set(p.item_code, { 
                      value: p.item_code, 
                      label: p.item_code, 
                      product: p 
                    });
                  }
                });
                return Array.from(uniqueCodes.values());
              })()}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.label || '';
              }}
              isOptionEqualToValue={(option, value) => {
                if (typeof option === 'string' && typeof value === 'string') return option === value;
                if (typeof option === 'object' && typeof value === 'object') {
                  return option.value === value.value;
                }
                return false;
              }}
              value={formData.item_code}
              onChange={(_, option) => {
                if (option && typeof option === 'object') {
                  handleFieldChange('item_code', option.value);
                  if (option.product) {
                    handleFieldChange('item_name', option.product.item_name);
                    handleFieldChange('rate', option.product.rate);
                    handleFieldChange('gst_percentage', option.product.gst_percentage);
                    handleFieldChange('hsn_code', option.product.hsn_code);
                  }
                } else if (typeof option === 'string') {
                  handleFieldChange('item_code', option);
                } else {
                  handleFieldChange('item_code', '');
                }
              }}
              onInputChange={(_, value) => handleFieldChange('item_code', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Part # *"
                  placeholder="Part #"
                  error={!!validationErrors.item_code}
                  helperText={validationErrors.item_code}
                  disabled={disabled || submitting || loading}
                  size="small"
                  variant="outlined"
                  className="spf-compact-input"
                  inputRef={(el) => {
                    // Preserve MUI's internal ref if present
                    try {
                      // params.inputProps.ref may be a function or object
                      const maybeRef: any = params.inputProps && (params.inputProps as any).ref;
                      if (typeof maybeRef === 'function') maybeRef(el);
                      else if (maybeRef && typeof maybeRef === 'object') maybeRef.current = el;
                    } catch (e) {
                      // ignore
                    }
                    itemCodeInputRef.current = el;
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: existingProduct ? (
                      <InputAdornment position="end">
                        <CheckIcon color="success" fontSize="small" />
                      </InputAdornment>
                    ) : params.InputProps.endAdornment,
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" {...otherProps} key={option.value}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {option.label}
                      </Typography>
                      {option.product && (
                        <Typography variant="caption" color="text.secondary">
                          {option.product.item_name} &bull; ₹{option.product.rate}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              freeSolo
              selectOnFocus
              clearOnBlur
            />
          </Grid>

          {/* Part Description */}
          <Grid item xs={6} sm={5} md={3}>
            <Autocomplete
              options={products.filter(p => p.item_name).map(p => ({
                value: p._id,
                label: p.item_name,
                product: p,
              }))}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.label || '';
              }}
              isOptionEqualToValue={(option, value) => {
                if (typeof option === 'string' && typeof value === 'string') return option === value;
                if (typeof option === 'object' && typeof value === 'object') {
                  return option.value === value.value;
                }
                return false;
              }}
              value={formData.item_name}
              onChange={(_, option) => {
                if (option && typeof option === 'object') {
                  skipItemCodeLookup.current = true;
                  handleFieldChange('item_name', option.label);
                  if (option.product) {
                    handleFieldChange('item_code', option.product.item_code);
                    handleFieldChange('rate', option.product.rate);
                    handleFieldChange('gst_percentage', option.product.gst_percentage);
                    handleFieldChange('hsn_code', option.product.hsn_code);
                    setExistingProduct(option.product);
                  }
                } else if (typeof option === 'string') {
                  handleFieldChange('item_name', option);
                } else {
                  handleFieldChange('item_name', '');
                }
              }}
              onInputChange={(_, value) => handleFieldChange('item_name', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Description *"
                  placeholder="Description"
                  error={!!validationErrors.item_name}
                  helperText={validationErrors.item_name}
                  disabled={disabled || submitting || loading}
                  size="small"
                  variant="outlined"
                  className="spf-compact-input"
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" {...otherProps} key={option.value}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {option.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.product.item_code} &bull; ₹{option.product.rate} &bull; GST {option.product.gst_percentage}%
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              freeSolo
              selectOnFocus
              clearOnBlur
            />
          </Grid>

          {/* Quantity */}
          <Grid item xs={4} sm={2} md={1}>
            <TextField
              label="Qty *"
              type="number"
              value={formData.quantity > 0 ? formData.quantity : ''}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                handleFieldChange('quantity', isNaN(val) ? 0 : Math.max(0, val));
              }}
              error={!!validationErrors.quantity}
              helperText={validationErrors.quantity}
              disabled={disabled || submitting || loading}
              fullWidth
              size="small"
              variant="outlined"
              className="spf-compact-input"
              placeholder="1"
            />
          </Grid>

          {/* Rate */}
          <Grid item xs={4} sm={2} md={1.5}>
            <TextField
              label="Rate *"
              type="number"
              value={formData.rate > 0 ? formData.rate : ''}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                handleFieldChange('rate', isNaN(val) ? 0 : Math.max(0, val));
              }}
              error={!!validationErrors.rate}
              helperText={validationErrors.rate}
              disabled={disabled || submitting || loading}
              fullWidth
              size="small"
              variant="outlined"
              className="spf-compact-input"
              placeholder="0.00"
              InputProps={{
                startAdornment: <InputAdornment position="start" sx={{ fontSize: '0.75rem' }}>₹</InputAdornment>,
              }}
            />
          </Grid>

          {/* GST Percentage */}
          <Grid item xs={4} sm={2} md={1}>
            <TextField
              label="GST %"
              type="number"
              value={formData.gst_percentage || ''}
              onChange={(e) => handleFieldChange('gst_percentage', parseFloat(e.target.value) || 0)}
              error={!!validationErrors.gst_percentage}
              helperText={validationErrors.gst_percentage}
              disabled={disabled || submitting || loading}
              fullWidth
              size="small"
              variant="outlined"
              className="spf-compact-input"
              placeholder="18"
              InputProps={{
                endAdornment: <InputAdornment position="end" sx={{ fontSize: '0.75rem' }}>%</InputAdornment>,
              }}
            />
          </Grid>

          {/* PO Number */}
          <Grid item xs={4} sm={2} md={1.2}>
            <TextField
              label="PO"
              value={formData.poNumber}
              onChange={(e) => handleFieldChange('poNumber', e.target.value)}
              disabled={disabled || submitting || loading}
              fullWidth
              size="small"
              variant="outlined"
              className="spf-compact-input"
              placeholder="PO"
            />
          </Grid>

          {/* HSN Code */}
          <Grid item xs={4} sm={2} md={1.3}>
            <TextField
              label="HSN"
              type="number"
              value={formData.hsn_code || ''}
              onChange={(e) => handleFieldChange('hsn_code', parseInt(e.target.value) || 0)}
              disabled={disabled || submitting || loading}
              fullWidth
              size="small"
              variant="outlined"
              className="spf-compact-input"
              placeholder="0"
            />
          </Grid>

          {/* Total Display */}
          <Grid item xs={4} sm={3} md={1.5}>
            <Box className="spf-total-display">
              <Typography variant="caption" className="spf-total-label">Total</Typography>
              <Typography variant="body2" className="spf-total-value">
                {formatCurrency(formData.total)}
              </Typography>
            </Box>
          </Grid>

          {/* Smart Add Button */}
          <Grid item xs={12} sm={4} md={1.5}>
            <Button
              variant="contained"
              onClick={handleSmartAdd}
              disabled={disabled || loading || submitting || !isFormValid}
              startIcon={submitting ? <CircularProgress size={16} /> : <AddIcon fontSize="small" />}
              fullWidth
              size="small"
              className="spf-smart-add-button"
            >
              {submitting ? 'Processing...' : 'Add'}
            </Button>
          </Grid>
        </Grid>

          {/* Action Type Indicator */}
          {formData.item_code && (
            <Box className="spf-action-indicator">
              <Chip
                icon={
                  smartActionType === 'create' ? <AddIcon /> :
                  smartActionType === 'update' ? <RefreshIcon /> :
                  <CheckIcon />
                }
                label={
                  smartActionType === 'create' ? 'Create New Product' :
                  smartActionType === 'update' ? 'Update Existing Product' :
                  'Add Existing Product'
                }
                color={
                  smartActionType === 'create' ? 'warning' :
                  smartActionType === 'update' ? 'info' :
                  'success'
                }
                variant="outlined"
                size="small"
              />
            </Box>
          )}
        </>
        )}
      </Box>

    </Box>
  );
};

export default SmartProductForm; 